import React, { useState } from 'react';
import { Send, Bold, Italic, List, Link, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export default function RichReplyComposer({ onSend, loading, recipient, threadSubject }) {
    const [body, setBody] = useState('');
    const [stopAutomations, setStopAutomations] = useState(true);

    const handleSend = () => {
        if (!body.trim()) return;
        onSend({ body, stopAutomations });
    };

    return (
        <div className="flex flex-col gap-4 border rounded-2xl bg-card overflow-hidden shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-b">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">To:</span>
                    <Badge variant="secondary" className="text-[10px] font-bold">{recipient}</Badge>
                </div>
                <div className="flex items-center gap-1">
                    <button className="p-1.5 hover:bg-accent rounded-md text-muted-foreground transition-colors"><Bold className="h-3.5 w-3.5" /></button>
                    <button className="p-1.5 hover:bg-accent rounded-md text-muted-foreground transition-colors"><Italic className="h-3.5 w-3.5" /></button>
                    <button className="p-1.5 hover:bg-accent rounded-md text-muted-foreground transition-colors"><List className="h-3.5 w-3.5" /></button>
                    <button className="p-1.5 hover:bg-accent rounded-md text-muted-foreground transition-colors"><Link className="h-3.5 w-3.5" /></button>
                </div>
            </div>

            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={`Replying to "${threadSubject}"...`}
                className="w-full p-4 min-h-[180px] text-sm bg-transparent outline-none resize-none font-outfit leading-relaxed"
                disabled={loading}
            />

            <div className="flex items-center justify-between px-4 py-3 bg-muted/10 border-t">
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer group">
                        <input 
                            type="checkbox" 
                            checked={stopAutomations} 
                            onChange={(e) => setStopAutomations(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-[11px] font-bold text-muted-foreground group-hover:text-foreground transition-colors">Stop Automations</span>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="h-3 w-3 text-muted-foreground/40" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-[10px] max-w-[200px]">If enabled, all future automated follow-ups for this contact will be paused.</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </label>
                </div>

                <div className="flex items-center gap-2">
                    <Button 
                        size="sm" 
                        variant="default" 
                        className="gap-2 px-6 h-9 rounded-full shadow-lg hvr-grow" 
                        onClick={handleSend}
                        disabled={loading || !body.trim()}
                    >
                        {loading ? <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Send Reply
                    </Button>
                </div>
            </div>
        </div>
    );
}
